import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, Lightbulb, Link2, Layers, BookOpen, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { usePhrases } from '@/context/PhrasesContext';
import { useLanguage } from '@/context/LanguageContext';
import { getCategoryColor } from '@/utils/categoryColors';
import { DinoLlamaLoader } from '@/components/DinoLlamaLoader';
import { apiFetch } from '@/hooks/useFetch';

export const MultiCategoryPhraseDetail = ({ phraseData }) => {
  const navigate = useNavigate();
  const { categories, phrases } = usePhrases();
  const { t, isSpanish, translateCategories, translatePhraseContent } = useLanguage();
  const [translatedVariations, setTranslatedVariations] = useState([]);
  const [translatedCats, setTranslatedCats] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  
  // Audio state
  const [hasAudio, setHasAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  
  const { phrase: phraseText, variations } = phraseData;
  
  // Use the first variation's ID for audio lookup
  const primaryPhraseId = variations?.[0]?.id;
  
  // Check if audio is available for this phrase
  useEffect(() => {
    const checkAudio = async () => {
      if (!primaryPhraseId) return;
      
      try {
        const { data, error } = await apiFetch(`/api/audio/check/${primaryPhraseId}`);
        if (!error && data) {
          setHasAudio(data.has_audio);
          if (data.has_audio) {
            const { data: audioData, error: audioError } = await apiFetch(`/api/audio/phrase/${primaryPhraseId}`);
            if (!audioError && audioData) {
              setAudioUrl(audioData.audio_url);
            }
          }
        }
      } catch (error) {
        console.error('Error checking audio:', error);
        setHasAudio(false);
      }
    };
    
    checkAudio();
    
    // Reset audio state when phrase changes
    return () => {
      setIsPlaying(false);
      setAudioUrl(null);
      setHasAudio(false);
    };
  }, [primaryPhraseId]);
  
  // Handle audio playback
  const handlePlayAudio = async () => {
    if (!audioUrl) return;
    
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }
    
    setIsLoadingAudio(true);
    
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(audioUrl);
        audioRef.current.onended = () => setIsPlaying(false);
        audioRef.current.onerror = () => {
          setIsPlaying(false);
          setIsLoadingAudio(false);
          console.error('Audio playback error');
        };
      }
      
      await audioRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('Error playing audio:', error);
    } finally {
      setIsLoadingAudio(false);
    }
  };
  
  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);
  
  // Translate variations and categories when language changes
  useEffect(() => {
    const doTranslate = async () => {
      // Check if any phrase is from a Spanish category
      const hasSpanishCategoryPhrase = variations?.some(v => 
        v.category?.startsWith('es-') || v.category?.startsWith('spanish-')
      );
      
      if ((isSpanish || hasSpanishCategoryPhrase) && variations && variations.length > 0) {
        setIsTranslating(true);
        try {
          // Translate each variation
          const translatedVars = await Promise.all(
            variations.map(v => translatePhraseContent(v))
          );
          setTranslatedVariations(translatedVars);
          
          // Translate categories
          const uniqueCatIds = [...new Set(variations.map(v => v.category))];
          const catsToTranslate = uniqueCatIds.map(id => categories.find(c => c.id === id)).filter(Boolean);
          const translated = await translateCategories(catsToTranslate);
          const catMap = {};
          translated.forEach(cat => { catMap[cat.id] = cat; });
          setTranslatedCats(catMap);
        } finally {
          setIsTranslating(false);
        }
      } else {
        setTranslatedVariations([]);
        setTranslatedCats({});
      }
    };
    doTranslate();
  }, [variations, isSpanish, translatePhraseContent, translateCategories, categories]);
  
  // Use translated or original
  const displayVariations = (isSpanish || variations?.some(v => v.category?.startsWith('es-'))) && translatedVariations.length > 0 
    ? translatedVariations 
    : variations;
    
  const getCategoryName = (catId) => {
    if (isSpanish && translatedCats[catId]) {
      return translatedCats[catId].name;
    }
    return categories.find(c => c.id === catId)?.name || catId;
  };
  
  // Helper function to normalize meaning for comparison
  // This removes minor differences like "really good" vs "great", punctuation, etc.
  const normalizeMeaning = (meaning) => {
    if (!meaning) return '';
    return meaning
      .toLowerCase()
      .trim()
      // Remove common filler words and punctuation (but keep "or" for compound meanings)
      .replace(/[''""".,!?;:()'-]/g, '')
      // Normalize common synonyms
      .replace(/\breally good\b/g, 'great')
      .replace(/\bcatchy\b/g, '')
      .replace(/\bvery\b/g, '')
      .replace(/\breally\b/g, '')
      // Normalize sausage variations
      .replace(/\bpork sausage\b/g, 'sausage')
      .replace(/\bbeatenup\b/g, 'old')
      .replace(/\bbeaten up\b/g, 'old')
      .replace(/\bsimilar to british usage\b/g, '')
      .replace(/\bas in bangers and mash\b/g, '')
      // Remove "a/an" at start
      .replace(/^(a|an)\s+/g, '')
      // Normalize spacing
      .replace(/\s+/g, ' ')
      .trim();
  };
  
  // Check if meaning is a compound (has "or" indicating multiple meanings)
  const isCompoundMeaning = (meaning) => {
    const normalized = normalizeMeaning(meaning);
    return normalized.includes(' or ');
  };
  
  // Extract core meaning keywords for comparison
  const extractCoreKeywords = (meaning) => {
    const normalized = normalizeMeaning(meaning);
    // Extract key nouns/concepts
    const keywords = new Set();
    if (normalized.includes('sausage')) keywords.add('sausage');
    if (normalized.includes('song')) keywords.add('song');
    if (normalized.includes('great') || normalized.includes('good') || normalized.includes('hit')) keywords.add('great');
    if (normalized.includes('car')) keywords.add('car');
    if (normalized.includes('firework') || normalized.includes('firecracker')) keywords.add('firework');
    return keywords;
  };
  
  // Check if two meanings are similar enough to consolidate
  const areMeaningsSimilar = (meaning1, meaning2) => {
    const norm1 = normalizeMeaning(meaning1);
    const norm2 = normalizeMeaning(meaning2);
    
    // Exact match after normalization
    if (norm1 === norm2) return true;
    
    // Only merge if one meaning is a very close subset of the other
    // (e.g. "Barbecue" and "Barbecue - outdoor cooking" can merge)
    // But "A sweet cookie" and "A bread roll" should NOT merge
    
    // Short meanings: must be exact or one fully contains the other with minimal difference
    if (norm1.length < 40 && norm2.length < 40) {
      // Only merge if one is exactly contained in the other AND they share 80%+ length
      if (norm1.includes(norm2) && norm2.length / norm1.length > 0.7) return true;
      if (norm2.includes(norm1) && norm1.length / norm2.length > 0.7) return true;
      return false;
    }
    
    // Longer meanings: check if one fully contains the other
    if (norm1.includes(norm2) && norm2.length / norm1.length > 0.6) return true;
    if (norm2.includes(norm1) && norm1.length / norm2.length > 0.6) return true;
    
    return false;
  };
  
  // Group variations by similar meanings
  // This allows categories with essentially the same meaning to be consolidated
  const groupedDefinitions = useMemo(() => {
    if (!variations || variations.length === 0) return [];
    
    const groups = [];
    
    // Group by similar meanings
    variations.forEach((variation, idx) => {
      // Get the display version (translated if available)
      const displayVariation = displayVariations?.[idx] || variation;
      
      // Check if we have a similar meaning already in any group
      let foundGroupIndex = -1;
      for (let i = 0; i < groups.length; i++) {
        if (areMeaningsSimilar(variation.actualMeaning, groups[i].variation.actualMeaning)) {
          foundGroupIndex = i;
          break;
        }
      }
      
      if (foundGroupIndex >= 0) {
        // Add this category to existing group
        groups[foundGroupIndex].categories.push({
          id: variation.category,
          name: getCategoryName(variation.category),
          phraseId: variation.id
        });
      } else {
        // Create new group - use display variation for content
        groups.push({
          variation: displayVariation,
          categories: [{
            id: variation.category,
            name: getCategoryName(variation.category),
            phraseId: variation.id
          }]
        });
      }
    });
    
    return groups;
  }, [variations, displayVariations, getCategoryName]);
  // Get unique related phrases from all variations
  const allRelatedPhrases = [...new Set(
    variations.flatMap(v => v.relatedPhrases || [])
  )];
  
  // Get unique thesaurus words from all variations (actual thesaurus field from database)
  const allThesaurusWords = [...new Set(
    variations.flatMap(v => v.thesaurus || [])
  )];
  
  // Separate related phrases into two groups:
  // 1. Defined phrases (exist in the app) - clickable links
  // 2. Other related phrases (not in app) - non-clickable badges
  const { definedPhrases, otherRelated, thesaurusWords } = useMemo(() => {
    const defined = [];
    const other = [];
    
    // Process related phrases
    allRelatedPhrases.forEach(related => {
      // Check if this phrase exists in the database (exact match, case-insensitive)
      const exists = phrases.some(p => 
        p.phrase.toLowerCase() === related.toLowerCase()
      );
      
      if (exists) {
        defined.push(related);
      } else {
        other.push(related);
      }
    });
    
    // Use actual thesaurus field from database
    return { definedPhrases: defined, otherRelated: other, thesaurusWords: allThesaurusWords };
  }, [allRelatedPhrases, allThesaurusWords, phrases]);
  
  // Navigate to a defined phrase
  const handleRelatedPhraseClick = (relatedPhraseName) => {
    const matchedPhrase = phrases.find(p => 
      p.phrase.toLowerCase() === relatedPhraseName.toLowerCase()
    );
    
    if (matchedPhrase) {
      navigate(`/phrase/${matchedPhrase.id}`);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Translation indicator with Dino Llama */}
      {isTranslating && (
        <DinoLlamaLoader message={t('translating')} size="sm" />
      )}
      
      {/* Main Phrase Card with All Categories */}
      <Card className="border-2">
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <Layers className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{t('foundInMultipleCategories')}</span>
            </div>
            
            {/* Audio Playback Button */}
            {hasAudio && (
              <Button
                variant="outline"
                size="sm"
                onClick={handlePlayAudio}
                disabled={isLoadingAudio}
                className="flex items-center gap-2 border-2 border-[#00E5CC] bg-[#00E5CC]/20 text-[#00E5CC] font-bold hover:bg-[#00E5CC]/30 hover:border-[#00E5CC] shadow-lg transition-all"
                data-testid="audio-play-btn"
              >
                {isLoadingAudio ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : isPlaying ? (
                  <VolumeX className="h-5 w-5" />
                ) : (
                  <Volume2 className="h-5 w-5" />
                )}
                <span>
                  {isPlaying ? (isSpanish ? 'Detener' : 'Stop') : (isSpanish ? 'Escuchar' : 'Listen')}
                </span>
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {displayVariations.map((variation, idx) => {
              const colors = getCategoryColor(variation.category);
              return (
                <span 
                  key={variation.id || idx}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colors.bg} ${colors.text}`}
                >
                  {getCategoryName(variation.category)}
                </span>
              );
            })}
          </div>
          <CardTitle className="text-3xl sm:text-4xl font-bold leading-tight">
            {phraseText}
          </CardTitle>
        </CardHeader>
      </Card>
      
      {/* Meanings - Grouped by identical definitions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-primary" />
            <span>{groupedDefinitions.length === 1 ? t('meaning') : t('meaningsByCategory')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {groupedDefinitions.map((group, groupIdx) => {
            const { variation, categories: groupCategories } = group;
            const isShared = groupCategories.length > 1;
            
            return (
              <div 
                key={groupIdx} 
                className={`${groupIdx > 0 ? 'border-t pt-6' : ''}`}
              >
                {/* Category badges - shown side by side when definition is shared */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {groupCategories.map((cat, catIdx) => {
                    const colors = getCategoryColor(cat.id);
                    return (
                      <span 
                        key={catIdx}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colors.bg} ${colors.text}`}
                      >
                        {cat.name}
                      </span>
                    );
                  })}
                </div>
                
                {/* Definition content */}
                <div className="space-y-4 pl-2">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('literalMeaning')}</p>
                    <p className="text-base text-foreground/80">{variation.literalMeaning}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('actualMeaning')}</p>
                    <p className="text-base font-medium text-foreground">{variation.actualMeaning}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('context')}</p>
                    <p className="text-base text-foreground/80">{variation.context}</p>
                  </div>
                  
                  {variation.examples && variation.examples.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('examples')}</p>
                      <ul className="space-y-1">
                        {variation.examples.map((example, exIdx) => (
                          <li key={exIdx} className="text-base text-foreground/80 italic">"{example}"</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {variation.tips && variation.tips.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('tips')}</p>
                      <ul className="list-disc list-inside space-y-1">
                        {variation.tips.map((tip, tipIdx) => (
                          <li key={tipIdx} className="text-base text-foreground/80">{tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
      
      {/* Phrase Thesaurus - Synonyms not defined in app */}
      {thesaurusWords.length > 0 && (
        <Card className="border-secondary/30 bg-secondary/5">
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <BookOpen className="h-5 w-5 text-secondary-foreground" />
              <span>{t('phraseThesaurus')}</span>
            </CardTitle>
            <CardDescription className="text-sm">
              {t('similarWords')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {thesaurusWords.map((word, index) => (
                <Badge 
                  key={index} 
                  variant="secondary"
                  className="text-xs px-2 py-0.5"
                >
                  {word}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Related Phrases - Phrases defined in app (clickable) and other related phrases */}
      {(definedPhrases.length > 0 || otherRelated.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <Link2 className="h-5 w-5 text-secondary-foreground" />
              <span>{t('relatedPhrases')}</span>
            </CardTitle>
            {definedPhrases.length > 0 && (
              <CardDescription className="text-sm">
                {t('clickToExplore')}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {/* Clickable phrases that exist in the app */}
              {definedPhrases.map((related, index) => (
                <Badge 
                  key={`defined-${index}`} 
                  variant="secondary" 
                  className="text-xs px-2 py-0.5 cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors border border-border"
                  onClick={() => handleRelatedPhraseClick(related)}
                >
                  {related}
                </Badge>
              ))}
              {/* Non-clickable related phrases (not in app database) */}
              {otherRelated.map((related, index) => (
                <Badge 
                  key={`other-${index}`} 
                  variant="secondary" 
                  className="text-xs px-2 py-0.5"
                >
                  {related}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
